import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { Toaster } from "react-hot-toast";

import App from "./App";
import { ChangelogDetailPage } from "./pages/ChangelogDetailPage";
import { AdminLayout } from "./pages/admin/AdminLayout";
import { AdminLoginPage } from "./pages/admin/AdminLoginPage";
import { AdminReleasesPage } from "./pages/admin/AdminReleasesPage";
import { AdminGeneratePage } from "./pages/admin/AdminGeneratePage";
import { AdminAiConfigPage } from "./pages/admin/AdminAiConfigPage";
import { RootLayout } from "./components/RootLayout";
import { ThemeProvider } from "./hooks/useTheme";
import { AdminAuthProvider, useAdminAuth } from "./hooks/useAdminAuth";
import { TooltipProvider } from "./components/ui/Tooltip";
import "./index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

const AdminGate: React.FC = () => {
  const { isAuthenticated } = useAdminAuth();
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location.pathname }} replace />;
  }
  return <AdminLayout />;
};

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
        <AdminAuthProvider>
          <TooltipProvider delayDuration={400}>
            <BrowserRouter>
              <Routes>
                <Route element={<RootLayout />}>
                  <Route path="/" element={<App />} />
                  <Route path="/changelog/:id" element={<ChangelogDetailPage />} />
                </Route>

                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route path="/admin" element={<AdminGate />}>
                  <Route index element={<Navigate to="releases" replace />} />
                  <Route path="releases" element={<AdminReleasesPage />} />
                  <Route path="generate" element={<AdminGeneratePage />} />
                  <Route path="ai" element={<AdminAiConfigPage />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
            <Toaster
              position="top-center"
              toastOptions={{
                duration: 3500,
                className: "!bg-foreground !text-background !border-0 !rounded-md !text-sm !shadow-lg",
                success: {
                  iconTheme: { primary: "hsl(150 35% 45%)", secondary: "hsl(var(--background))" },
                },
                error: {
                  iconTheme: { primary: "hsl(8 60% 50%)", secondary: "hsl(var(--background))" },
                },
              }}
            />
          </TooltipProvider>
        </AdminAuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
